import React, { useState, useRef, useEffect, useCallback } from "react";
import "../styles/ThemedDropdown.scss";

/**
 * ThemedDropdown — a modern, theme-matched replacement for a native <select>,
 * styled like the claim-details / file-claim screens (Inter, #DDE1E8 borders,
 * orange accent, rounded, soft-shadow menu).
 *
 * Props:
 *   value       - the selected option's value (primitive; compared with ===)
 *   onChange    - (value) => void, called with the chosen option's value
 *   options     - [{ value, label, disabled? }]
 *   placeholder - shown when nothing is selected (default "Select")
 *   disabled    - disables the control
 *   className   - extra class(es); pass "td-compact" for the small toolbar variant
 *   autoOpen    - open the menu on mount; for dropdowns that only get rendered
 *                 once an edit affordance (e.g. a pencil) has been clicked, so
 *                 the user doesn't have to click a second time to open them
 *   onClose     - called whenever the menu closes, however it closed (pick,
 *                 outside click, Escape, trigger toggle). Lets the caller drop
 *                 the dropdown and go back to its read-only rendering.
 */
function ThemedDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  disabled = false,
  className = "",
  autoOpen = false,
  onClose,
}) {
  const [open, setOpen] = useState(autoOpen && !disabled);
  const rootRef = useRef(null);

  // Held in a ref so `close` keeps a stable identity even when the caller
  // passes an inline arrow — otherwise the listener effect below tears down
  // and re-subscribes on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const close = useCallback(() => {
    setOpen(false);
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const selected = options.find((o) => o.value === value);

  const pick = (opt) => {
    if (opt.disabled) return;
    if (onChange) onChange(opt.value);
    close();
  };

  const cls =
    "themed-dropdown" +
    (open ? " open" : "") +
    (disabled ? " disabled" : "") +
    (className ? " " + className : "");

  return (
    <div className={cls} ref={rootRef}>
      <button
        type="button"
        className="td-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          if (open) close();
          else setOpen(true);
        }}
      >
        <span className={"td-value" + (selected ? "" : " placeholder")}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="td-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="td-menu" role="listbox">
          {options.length === 0 ? (
            <div className="td-empty">No options</div>
          ) : (
            options.map((o) => (
              <div
                key={String(o.value)}
                role="option"
                aria-selected={o.value === value}
                className={
                  "td-option" +
                  (o.value === value ? " selected" : "") +
                  (o.disabled ? " disabled" : "")
                }
                onClick={() => pick(o)}
              >
                <span className="td-option-label">{o.label}</span>
                {o.value === value && (
                  <svg className="td-check" width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <path d="M13 4.5L6.5 11L3 7.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default ThemedDropdown;
